import React, { useEffect, useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, MessageCircle, Send, Link as LinkIcon, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';

/**
 * One-click social share row.
 *
 * Renders an inline strip of share buttons (Facebook, X/Twitter, LinkedIn,
 * WhatsApp, Telegram, Copy Link) plus the native Web Share API button on
 * mobile devices that support it. Each opens the platform's share dialog
 * pre-filled with the page URL + title.
 *
 * Behaviour:
 *   • Web Share button only appears when navigator.share is available
 *     (Android Chrome, iOS Safari) — falls back gracefully otherwise.
 *   • Copy Link writes to clipboard with a 2-second "copied" affordance.
 *   • Each share window opens in a small popup (Twitter intent style)
 *     so the user isn't navigated away from your site.
 *
 * Drop-in usage:
 *   <ShareButtons url={fullUrl} title={post.title}
 *                 hashtag="Shopperden" via="shopperdenuk" />
 */
const ShareButtons = ({
  url,
  title = '',
  hashtag = 'Shopperden',
  via = 'shopperdenuk',
  size = 'md',
  label = 'Share',
  dataTestId = 'share-buttons',
}) => {
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      setHasNativeShare(true);
    }
  }, []);

  const enc = (s) => encodeURIComponent(s || '');
  const u = enc(url);
  const t = enc(title);

  const open = (href) => {
    const w = 600, h = 540;
    const left = Math.max(0, (window.innerWidth - w) / 2);
    const top = Math.max(0, (window.innerHeight - h) / 2);
    window.open(href, 'share-popup', `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,scrollbars=yes`);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — long-press the link to copy manually.');
    }
  };

  const handleNative = async () => {
    try {
      await navigator.share({ title, url, text: title });
    } catch { /* user cancelled — silent */ }
  };

  const icon = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const btn = `inline-flex items-center justify-center transition-all ${
    size === 'sm' ? 'h-8 w-8 rounded' : 'h-10 w-10 rounded-md'
  } hover:scale-105 hover:shadow-sm`;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={dataTestId}>
      {label && (
        <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mr-1 flex items-center gap-1.5">
          <Share2 className="h-3.5 w-3.5" />
          {label}
        </span>
      )}

      <button
        type="button"
        onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${u}`)}
        className={`${btn} bg-[#1877F2] hover:bg-[#0E5FC4] text-white`}
        title="Share on Facebook"
        aria-label="Share on Facebook"
        data-testid={`${dataTestId}-facebook`}
      >
        <Facebook className={icon} />
      </button>

      <button
        type="button"
        onClick={() => open(`https://twitter.com/intent/tweet?url=${u}&text=${t}&hashtags=${hashtag}&via=${via}`)}
        className={`${btn} bg-black hover:bg-[#1A1A1A] text-white`}
        title="Share on X (Twitter)"
        aria-label="Share on X (Twitter)"
        data-testid={`${dataTestId}-twitter`}
      >
        <Twitter className={icon} />
      </button>

      <button
        type="button"
        onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`)}
        className={`${btn} bg-[#0A66C2] hover:bg-[#084E96] text-white`}
        title="Share on LinkedIn"
        aria-label="Share on LinkedIn"
        data-testid={`${dataTestId}-linkedin`}
      >
        <Linkedin className={icon} />
      </button>

      <a
        href={`https://api.whatsapp.com/send?text=${t}%20${u}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btn} bg-[#25D366] hover:bg-[#1FAE52] text-white`}
        title="Share on WhatsApp"
        aria-label="Share on WhatsApp"
        data-testid={`${dataTestId}-whatsapp`}
      >
        <MessageCircle className={icon} />
      </a>

      <button
        type="button"
        onClick={() => open(`https://t.me/share/url?url=${u}&text=${t}`)}
        className={`${btn} bg-[#229ED9] hover:bg-[#1A82B5] text-white`}
        title="Share on Telegram"
        aria-label="Share on Telegram"
        data-testid={`${dataTestId}-telegram`}
      >
        <Send className={icon} />
      </button>

      <a
        href={`mailto:?subject=${t}&body=${t}%0A%0A${u}`}
        className={`${btn} bg-slate-700 hover:bg-slate-900 text-white`}
        title="Share via Email"
        aria-label="Share via Email"
        data-testid={`${dataTestId}-email`}
      >
        <Mail className={icon} />
      </a>

      <button
        type="button"
        onClick={handleCopy}
        className={`${btn} bg-slate-100 hover:bg-slate-200 text-slate-700`}
        title="Copy link"
        aria-label="Copy link"
        data-testid={`${dataTestId}-copy`}
      >
        {copied ? <Check className={`${icon} text-emerald-600`} /> : <LinkIcon className={icon} />}
      </button>

      {hasNativeShare && (
        <button
          type="button"
          onClick={handleNative}
          className={`${btn} bg-[#E07A3A] hover:bg-[#C4652E] text-white`}
          title="More share options"
          aria-label="More share options"
          data-testid={`${dataTestId}-native`}
        >
          <Share2 className={icon} />
        </button>
      )}
    </div>
  );
};

export default ShareButtons;
